import { useState } from "react";
import { ApiError, runBacktest } from "@/api/client";
import type { BacktestGridResponse, BacktestResponse, BacktestSummaryResponse, Timeframe } from "@/api/types";
import StatTile from "@/components/StatTile";

interface BacktestPanelProps {
  open: boolean;
  onClose: () => void;
  defaultSymbols: string[];
}

const TIMEFRAMES: Timeframe[] = ["1h", "4h", "1d"];

function formatUsd(n: number): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}$${n.toFixed(2)}`;
}

function isGrid(res: BacktestResponse): res is BacktestGridResponse {
  return res.mode === "grid";
}

export default function BacktestPanel({ open, onClose, defaultSymbols }: BacktestPanelProps) {
  const [symbol, setSymbol] = useState(defaultSymbols[0] ?? "BTCUSDT");
  const [timeframe, setTimeframe] = useState<Timeframe>("1h");
  const [mode, setMode] = useState<"single" | "grid">("single");
  const [candleLimit, setCandleLimit] = useState(750);
  const [startingCapitalUsd, setStartingCapitalUsd] = useState(1000);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BacktestResponse | null>(null);

  async function handleRun() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await runBacktest({
        symbol: symbol.trim().toUpperCase(),
        timeframe,
        candleLimit,
        startingCapitalUsd,
        mode,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Backtest failed to run");
    } finally {
      setRunning(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-void/80 px-4 py-10 backdrop-blur-sm">
      <div className="w-full max-w-3xl rounded-lg border border-line bg-panel shadow-panel">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div className="font-mono text-sm font-semibold uppercase tracking-widest text-ink">
            Backtest
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-line px-2.5 py-1 font-mono text-xs text-ink-dim transition hover:border-sell/50 hover:text-sell"
          >
            Close
          </button>
        </div>

        <div className="max-h-[75vh] space-y-5 overflow-y-auto px-5 py-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">Symbol</span>
              <input
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                list="backtest-symbols"
                className="rounded-md border border-line bg-void px-2.5 py-1.5 font-mono text-xs text-ink outline-none focus:border-turquoise/60"
              />
              <datalist id="backtest-symbols">
                {defaultSymbols.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">Timeframe</span>
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value as Timeframe)}
                className="rounded-md border border-line bg-void px-2.5 py-1.5 font-mono text-xs text-ink outline-none focus:border-turquoise/60"
              >
                {TIMEFRAMES.map((tf) => (
                  <option key={tf} value={tf}>
                    {tf}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">Candles</span>
              <input
                type="number"
                min={100}
                max={1000}
                value={candleLimit}
                onChange={(e) => setCandleLimit(Number(e.target.value))}
                className="rounded-md border border-line bg-void px-2.5 py-1.5 font-mono text-xs text-ink outline-none focus:border-turquoise/60"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[11px] uppercase tracking-wide text-ink-faint">
                Starting Capital
              </span>
              <input
                type="number"
                min={1}
                value={startingCapitalUsd}
                onChange={(e) => setStartingCapitalUsd(Number(e.target.value))}
                className="rounded-md border border-line bg-void px-2.5 py-1.5 font-mono text-xs text-ink outline-none focus:border-turquoise/60"
              />
            </label>
          </div>

          <div className="flex items-center gap-1 border-b border-line pb-3">
            {(["single", "grid"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`rounded-md px-3 py-1.5 font-mono text-xs uppercase tracking-wide transition ${
                  mode === m
                    ? "border border-turquoise/60 text-turquoise"
                    : "border border-transparent text-ink-faint hover:text-ink-dim"
                }`}
              >
                {m === "single" ? "Single Run" : "Grid Search"}
              </button>
            ))}
            <span className="ml-auto font-mono text-[11px] text-ink-faint">
              {mode === "grid"
                ? "Sweeps entry threshold / exit threshold / min R:R, ranked by expectancy."
                : "Runs once with current settings-derived params."}
            </span>
          </div>

          <button
            onClick={handleRun}
            disabled={running || !symbol.trim()}
            className="rounded-md border border-turquoise/60 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-wide text-turquoise transition hover:bg-turquoise/10 disabled:opacity-50"
          >
            {running ? "Running…" : "Run Backtest"}
          </button>

          {error && (
            <div className="rounded-md border border-sell/40 bg-sell/10 px-3 py-2 font-mono text-xs text-sell">
              {error}
            </div>
          )}

          {result && !isGrid(result) && <SingleResult result={result} />}
          {result && isGrid(result) && <GridResult result={result} />}
        </div>
      </div>
    </div>
  );
}

function SingleResult({ result }: { result: BacktestSummaryResponse }) {
  const s = result.summary;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total Trades" value={String(s.totalTrades)} />
        <StatTile
          label="Win Rate"
          value={`${s.winRatePercent.toFixed(1)}%`}
          tone={s.winRatePercent >= 50 ? "profit" : "sell"}
        />
        <StatTile
          label="Total PnL"
          value={formatUsd(s.totalPnlUsd)}
          tone={s.totalPnlUsd >= 0 ? "profit" : "sell"}
        />
        <StatTile
          label="Return"
          value={`${s.totalReturnPercent >= 0 ? "+" : ""}${s.totalReturnPercent.toFixed(1)}%`}
          tone={s.totalReturnPercent >= 0 ? "profit" : "sell"}
        />
        <StatTile label="Expectancy" value={formatUsd(s.expectancyUsd)} />
        <StatTile label="Profit Factor" value={s.profitFactor.toFixed(2)} />
        <StatTile
          label="Max Drawdown"
          value={`${s.maxDrawdownPercent.toFixed(1)}%`}
          tone={s.maxDrawdownPercent > 20 ? "sell" : "default"}
        />
        <StatTile label="Sharpe" value={s.sharpeRatio.toFixed(2)} />
      </div>
      <div className="font-mono text-[11px] text-ink-faint">
        {result.candleCount} candles · final capital {formatUsd(s.finalCapitalUsd)}
      </div>
      {result.trades.length > 0 && (
        <div>
          <h4 className="font-mono text-xs font-semibold uppercase tracking-wide text-ink-dim">
            Trades (last {result.trades.length})
          </h4>
          <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-line">
            <table className="w-full font-mono text-[11px]">
              <thead className="sticky top-0 bg-panel text-ink-faint">
                <tr className="text-left">
                  <th className="px-2 py-1.5">Dir</th>
                  <th className="px-2 py-1.5">Entry</th>
                  <th className="px-2 py-1.5">Exit</th>
                  <th className="px-2 py-1.5">Reason</th>
                  <th className="px-2 py-1.5 text-right">PnL</th>
                </tr>
              </thead>
              <tbody>
                {result.trades.map((t, i) => (
                  <tr key={i} className="border-t border-line/60 text-ink-dim">
                    <td className={`px-2 py-1.5 ${t.direction === "long" ? "text-gold" : "text-turquoise"}`}>
                      {t.direction}
                    </td>
                    <td className="px-2 py-1.5 tabular">{t.entryPrice.toFixed(4)}</td>
                    <td className="px-2 py-1.5 tabular">{t.exitPrice.toFixed(4)}</td>
                    <td className="px-2 py-1.5">{t.exitReason}</td>
                    <td className={`px-2 py-1.5 text-right tabular ${t.pnlUsd >= 0 ? "text-profit" : "text-sell"}`}>
                      {formatUsd(t.pnlUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function GridResult({ result }: { result: BacktestGridResponse }) {
  return (
    <div>
      <div className="font-mono text-[11px] text-ink-faint">
        {result.symbol} · {result.timeframe} · {result.candleCount} candles · top {result.top10.length} by
        expectancy
      </div>
      <div className="mt-2 max-h-96 overflow-y-auto rounded-md border border-line">
        <table className="w-full font-mono text-[11px]">
          <thead className="sticky top-0 bg-panel text-ink-faint">
            <tr className="text-left">
              <th className="px-2 py-1.5">Entry ≥</th>
              <th className="px-2 py-1.5">Exit ≥</th>
              <th className="px-2 py-1.5">Min R:R</th>
              <th className="px-2 py-1.5 text-right">Trades</th>
              <th className="px-2 py-1.5 text-right">Win %</th>
              <th className="px-2 py-1.5 text-right">Expectancy</th>
              <th className="px-2 py-1.5 text-right">Return %</th>
              <th className="px-2 py-1.5 text-right">PF</th>
              <th className="px-2 py-1.5 text-right">Max DD</th>
              <th className="px-2 py-1.5 text-right">Sharpe</th>
            </tr>
          </thead>
          <tbody>
            {result.top10.map((row, i) => (
              <tr key={i} className="border-t border-line/60 text-ink-dim">
                <td className="px-2 py-1.5 tabular">{row.params.entryScoreThreshold}</td>
                <td className="px-2 py-1.5 tabular">{row.params.reversalExitThreshold}</td>
                <td className="px-2 py-1.5 tabular">{row.params.minRiskRewardRatio}</td>
                <td className="px-2 py-1.5 text-right tabular">{row.totalTrades}</td>
                <td className="px-2 py-1.5 text-right tabular">{row.winRatePercent.toFixed(1)}</td>
                <td
                  className={`px-2 py-1.5 text-right tabular ${row.expectancyUsd >= 0 ? "text-profit" : "text-sell"}`}
                >
                  {formatUsd(row.expectancyUsd)}
                </td>
                <td
                  className={`px-2 py-1.5 text-right tabular ${row.totalReturnPercent >= 0 ? "text-profit" : "text-sell"}`}
                >
                  {row.totalReturnPercent.toFixed(1)}
                </td>
                <td className="px-2 py-1.5 text-right tabular">{row.profitFactor.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right tabular">{row.maxDrawdownPercent.toFixed(1)}</td>
                <td className="px-2 py-1.5 text-right tabular">{row.sharpeRatio.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
